import { Component, OnInit, Inject, ViewChild, ElementRef, HostListener } from "@angular/core";
import { Constant } from "../core/constant";
import { AdminService } from "../services/admin.service";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { FailModuleComponent } from "../fail-module/fail-module.component";
import { EquipmentTypeList } from "../models/equipment-type-list";
import { EquipmentType } from "../models/equipment-type";
import { DeleteDialogComponent } from "../delete-dialog/delete-dialog.component";
import { MatMenuTrigger } from "@angular/material/menu";
import { Router } from "@angular/router";
import { CryptographyService } from "../services/cryptography.service";
import { LOCAL_STORAGE, WebStorageService } from "ngx-webstorage-service";
import { CustomEventNRService } from "../services/custom-event-nr.service";
import { SnackbarService } from "../services/snackbar.service";

@Component({
  selector: "app-view-all-equipment-type-list",
  templateUrl: "./view-all-equipment-type-list.component.html",
  styleUrls: ["./view-all-equipment-type-list.component.scss"],
})
export class ViewAllEquipmentTypeListComponent implements OnInit {
  activePopupId: string | number | null = null;
  loading: boolean = false;
  pageSizes: any[] = Constant.PageSizes;
  searchKey: string = "";
  userRole: any;

  page: number = 1;
  pageSize: number = 10;
  collectionSize: number = 0;
  totalPages: number = 0;

  equipmentTypeList: EquipmentTypeList = new EquipmentTypeList();
  allEquipmentTypes: EquipmentType[] = [];
  error: string = "";
  modalOption: NgbModalOptions = {};
  @ViewChild(MatMenuTrigger, { static: false }) trigger: MatMenuTrigger;
  @ViewChild("newEquipmentTypeInput", { static: false }) newEquipmentTypeInput: ElementRef;

  editingEquipmentTypeId: number | null = null;
  editEquipmentTypeName: string = "";
  editEquipmentTypeDescription: string = "";

  isAddingNewEquipmentType: boolean = false;
  newEquipmentTypeName: string = "";
  newEquipmentTypeDescription: string = "";

  constructor(
    private adminService: AdminService,
    private ngbModal: NgbModal,
    private router: Router,
    private cryptographyService: CryptographyService,
    @Inject(LOCAL_STORAGE)
    private storage: WebStorageService,
    private customEventNRService: CustomEventNRService,
    private snackbarService: SnackbarService,
  ) {}

  ngOnInit() {
    this.userRole = this.cryptographyService.decryptData(
      this.storage.get("userRole")
    );
    this.getEquipmentTypes();
  }

  selectedItemsPerPageChange(event) {
    this.page = 1;
    this.pageSize = event.value;
    this.getEquipmentTypes();
  }

  getEquipmentTypes(): void {
    this.loading = true;
    this.adminService.GetEquipmentTypeList(0, 10000, "").subscribe(
      (allData: EquipmentTypeList) => {
        this.allEquipmentTypes = (allData.equipmentTypes || []).map((o) => new EquipmentType(o));
      },
      () => {}
    );

    this.adminService
      .GetEquipmentTypeList(
        (this.page - 1) * this.pageSize,
        this.pageSize,
        this.searchKey
      )
      .subscribe(
        (data: EquipmentTypeList) => {
          this.equipmentTypeList.equipmentTypes = data.equipmentTypes.map(
            (o) => new EquipmentType(o)
          );
          this.collectionSize = data.count;
          this.totalPages = Math.ceil(this.collectionSize / this.pageSize);
          if (this.searchKey) {
            this.customEventNRService.sendSearchEvent(
              "GrsSearchEvents",
              this.searchKey,
              this.collectionSize,
              window.location.href
            );
          }
          this.loading = false;
        },
        (error: string) => {
          this.error = error;
          const modalRef = this.ngbModal.open(FailModuleComponent);
          modalRef.componentInstance.errorMessage = error;
          this.loading = false;
        }
      );
  }

  isNewEquipmentTypeNameDuplicate(): boolean {
    if (!this.newEquipmentTypeName || !this.newEquipmentTypeName.trim()) {
      return false;
    }
    const target = this.newEquipmentTypeName.trim().toLowerCase();
    return this.allEquipmentTypes.some(
      (e) => e.name && e.name.trim().toLowerCase() === target
    );
  }

  isEditEquipmentTypeNameDuplicate(): boolean {
    if (!this.editEquipmentTypeName || !this.editEquipmentTypeName.trim()) {
      return false;
    }
    const target = this.editEquipmentTypeName.trim().toLowerCase();
    return this.allEquipmentTypes.some(
      (e) => e.id !== this.editingEquipmentTypeId && e.name && e.name.trim().toLowerCase() === target
    );
  }

  addSearchKey(searchKey: string) {
    this.searchKey = searchKey;
    this.page = 1;
    this.getEquipmentTypes();
  }

  togglePopup(event: Event, item: any) {
    event.stopPropagation();
    const id = item.id;
    this.activePopupId = this.activePopupId === id ? null : id;
  }

  @HostListener("document:click", ["$event"])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest(".grid-actions")) {
      this.activePopupId = null;
    }
  }

  goToPreviousPage() {
    if (this.page > 1) {
      this.page--;
      this.getEquipmentTypes();
    }
  }

  goToNextPage() {
    if (this.page < this.totalPages) {
      this.page++;
      this.getEquipmentTypes();
    }
  }

  onAddEditEquipmentType(equipmentTypeId: number = null) {
    if (!equipmentTypeId) {
      this.startAddNewEquipmentType();
    } else {
      const item = this.equipmentTypeList.equipmentTypes.find((e) => e.id === equipmentTypeId);
      if (item) {
        this.onEdit(item);
      }
    }
  }

  startAddNewEquipmentType() {
    this.cancelInlineEdit();
    this.isAddingNewEquipmentType = true;
    this.newEquipmentTypeName = "";
    this.newEquipmentTypeDescription = "";

    setTimeout(() => {
      if (this.newEquipmentTypeInput && this.newEquipmentTypeInput.nativeElement) {
        this.newEquipmentTypeInput.nativeElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        this.newEquipmentTypeInput.nativeElement.focus();
      }
    }, 100);
  }

  cancelAddNewEquipmentType() {
    this.isAddingNewEquipmentType = false;
    this.newEquipmentTypeName = "";
    this.newEquipmentTypeDescription = "";
  }

  saveNewEquipmentType() {
    if (!this.newEquipmentTypeName || !this.newEquipmentTypeDescription || this.isNewEquipmentTypeNameDuplicate()) {
      return;
    }
    this.loading = true;
    const newObj = new EquipmentType();
    newObj.id = 0;
    newObj.name = this.newEquipmentTypeName.trim();
    newObj.description = this.newEquipmentTypeDescription.trim();

    this.adminService.AddEquipmentType(newObj).subscribe(
      (res: any) => {
        this.loading = false;
        if (res === false) {
          const modalRef = this.ngbModal.open(FailModuleComponent);
          modalRef.componentInstance.errorMessage = "Name or title already taken.";
          return;
        }
        this.isAddingNewEquipmentType = false;
        this.newEquipmentTypeName = "";
        this.newEquipmentTypeDescription = "";
        this.snackbarService.showSuccess("Equipment Type Added Successfully");
        this.getEquipmentTypes();
      },
      (err: any) => {
        this.loading = false;
        const modalRef = this.ngbModal.open(FailModuleComponent);
        modalRef.componentInstance.errorMessage =
          typeof err === "string" ? err : "Failed to add equipment type.";
      }
    );
  }

  onEdit(item: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.cancelAddNewEquipmentType();
    this.activePopupId = null;
    this.editingEquipmentTypeId = item.id;
    this.editEquipmentTypeName = item.name;
    this.editEquipmentTypeDescription = item.description;
  }

  cancelInlineEdit() {
    this.editingEquipmentTypeId = null;
    this.editEquipmentTypeName = "";
    this.editEquipmentTypeDescription = "";
  }

  saveInlineEquipmentType(item: any) {
    if (!this.editEquipmentTypeName || !this.editEquipmentTypeDescription || this.isEditEquipmentTypeNameDuplicate()) {
      return;
    }
    this.loading = true;
    const updated = new EquipmentType(item);
    updated.name = this.editEquipmentTypeName.trim();
    updated.description = this.editEquipmentTypeDescription.trim();

    this.adminService.UpdateEquipmentType(updated).subscribe(
      (res: any) => {
        this.loading = false;
        if (res === false) {
          const modalRef = this.ngbModal.open(FailModuleComponent);
          modalRef.componentInstance.errorMessage = "Name or title already taken.";
          return;
        }
        this.editingEquipmentTypeId = null;
        this.snackbarService.showSuccess("Equipment Type Updated Successfully");
        this.getEquipmentTypes();
      },
      (err: any) => {
        this.loading = false;
        const modalRef = this.ngbModal.open(FailModuleComponent);
        modalRef.componentInstance.errorMessage =
          typeof err === "string" ? err : "Failed to update equipment type.";
      }
    );
  }

  actionMenuOpen(event: Event) {
    event.stopPropagation();
  }

  closeMatMenuAndOpenDeleteModal(item, event) {
    if (this.trigger) {
      this.trigger.closeMenu();
    }
    setTimeout(() => {
      this.openDeleteModal(item, event);
    }, 100);
  }

  openDeleteModal(item, event) {
    event.stopPropagation();
    this.activePopupId = null;
    const modalRef = this.ngbModal.open(DeleteDialogComponent);
    modalRef.componentInstance.title =
      "Do you want to remove this Equipment Type from the list? ";
    modalRef.componentInstance.deleteItem = item;
    modalRef.componentInstance.message =
      "All the dependencies will be removed along with this Equipment Type";
    if (this.trigger) {
      this.trigger.closeMenu();
    }
    modalRef.componentInstance.deletePopupEmit.subscribe((deleteItem) => {
      if (deleteItem != null) {
        let equipmentType = new EquipmentType(item);
        this.loading = true;
        this.adminService
          .DeleteEquipmentType(equipmentType)
          .subscribe((data: any) => {
            this.loading = false;
            this.page = 1;
            this.getEquipmentTypes();
          });
      }
      this.ngbModal.dismissAll();
    });
  }
}
